import React from 'react';
import { useTranslation } from '../../../i18n';
import { translateMarketFilterLabel } from '../../../utils/marketTranslations';
// from 'react';
import { EventSnapshot } from '../../../api/types';
import { getCountryFlagUrl } from '../../../utils/flagUtils';
import { getEventName } from '../../../utils/eventHelpers';

// Merged catalog events are not pre-sorted; order by start time so tournament
// blocks and the matches inside them read chronologically.
function eventStartMs(ev: EventSnapshot): number {
  const t = ev.event?.scheduled_at ?? ev.event?.start_time;
  const n = t ? new Date(t).getTime() : NaN;
  return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
}

interface PreMatchListProps {
  title: string;
  events: EventSnapshot[];
  activeSport: string;
  setActiveSport: (s: string) => void;
  sportsTab: { id: string; name: string; emoji?: string }[];
  topMarket1: string | number;
  setTopMarket1: (m: string | number) => void;
  topMarket2: string | number;
  setTopMarket2: (m: string | number) => void;
  topMarket3: string | number;
  setTopMarket3: (m: string | number) => void;
  topMarket4: string | number;
  setTopMarket4: (m: string | number) => void;
  topMarket5: string | number;
  setTopMarket5: (m: string | number) => void;
  openDropdown: string | null;
  setOpenDropdown: (val: string | null | ((prev: string | null) => string | null)) => void;
  options: (string | number)[];
  renderMarketColHeader: (type: string | number, width: string) => React.ReactNode;
  renderMarketColCells: (ev: EventSnapshot, type: string | number, width: string) => React.ReactNode;
  handleEventClick: (ev: EventSnapshot) => void;
  formatTime: (iso: string) => string;
  onViewAll?: () => void;
  showViewAll?: boolean;
  hideSecondMarket?: boolean;
  loading?: boolean;
}

export const PreMatchList: React.FC<PreMatchListProps> = ({
  title,
  events,
  activeSport,
  setActiveSport,
  sportsTab,
  topMarket1,
  setTopMarket1,
  topMarket2,
  setTopMarket2,
  topMarket3,
  setTopMarket3,
  topMarket4,
  setTopMarket4,
  topMarket5,
  setTopMarket5,
  openDropdown,
  setOpenDropdown,
  options,
  renderMarketColHeader,
  renderMarketColCells,
  handleEventClick,
  formatTime,
  onViewAll,
  showViewAll = false,
  hideSecondMarket = false,
  loading = false
}) => {
  const { locale } = useTranslation();

  if (events.length === 0 && sportsTab.length <= 1) return null;

  return (
    <div className="mb-5">
      <h2 className="text-[15px] font-bold text-gray-900 mb-2 px-1">{title}</h2>
      <div className="bg-white rounded shadow-sm border border-gray-200 relative z-10">

        {/* Sport Tabs */}
        {sportsTab.length > 1 && (
          <div className="flex bg-white px-2 pt-1 border-b border-gray-200 overflow-x-auto scrollbar-hide">
            {sportsTab.map((s) => {
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveSport(s.id)}
                  className={`px-5 py-3 text-[14px] font-bold transition border-b-[3px] -mb-[1px] whitespace-nowrap ${activeSport === s.id
                      ? 'border-[#cc0000] text-gray-900'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                >
                  {s.emoji ? <span className="mr-1.5 text-[16px] leading-none">{s.emoji}</span> : null}
                  {s.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Table Dropdowns Top */}
        {events.length > 0 && (
          <div className="px-5 py-2 flex justify-end bg-[#f7f8f9] border-b border-gray-200 sticky top-[56px] z-20">
            <div className="relative">
              <div onClick={() => setOpenDropdown(o => o === 't1' ? null : 't1')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(topMarket1, locale)}</span>
                <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {openDropdown === 't1' && (
                <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                  <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                  {options.map(opt => (
                    <button key={opt} onClick={() => { setTopMarket1(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                      <div className="w-4">{topMarket1 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                      <span className={topMarket1 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={`relative${hideSecondMarket ? ' hidden min-[1500px]:block' : ''}`}>
              <div onClick={() => setOpenDropdown(o => o === 't2' ? null : 't2')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(topMarket2, locale)}</span>
                <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {openDropdown === 't2' && (
                <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                  <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                  {options.map(opt => (
                    <button key={opt} onClick={() => { setTopMarket2(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                      <div className="w-4">{topMarket2 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                      <span className={topMarket2 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={`relative ${hideSecondMarket ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>
              <div onClick={() => setOpenDropdown(o => o === 't3' ? null : 't3')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(topMarket3, locale)}</span>
                <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {openDropdown === 't3' && (
                <div className="absolute top-full left-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                  <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                  {options.map(opt => (
                    <button key={opt} onClick={() => { setTopMarket3(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                      <div className="w-4">{topMarket3 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                      <span className={topMarket3 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {!hideSecondMarket && (
              <div className="relative hidden min-[1650px]:block">
                <div onClick={() => setOpenDropdown(o => o === 't4' ? null : 't4')} className="w-[151px] mx-1 flex-shrink-0 bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                  <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(topMarket4, locale)}</span>
                  <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
                {openDropdown === 't4' && (
                  <div className="absolute top-full right-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                    <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                    {options.map(opt => (
                      <button key={opt} onClick={() => { setTopMarket4(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                        <div className="w-4">{topMarket4 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                        <span className={topMarket4 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!hideSecondMarket && (
                <div className="relative hidden min-[1750px]:block">
                  <div onClick={() => setOpenDropdown(o => o === `top5-${title}` ? null : `top5-${title}`)} className="w-[151px] mx-1 flex-shrink-0 bg-[#f0f2f5] rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                    <span className="text-[15px] font-bold text-gray-700 truncate">{translateMarketFilterLabel(topMarket5, locale)}</span>
                    <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                  {openDropdown === `top5-${title}` && (
                    <div className="absolute top-full right-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                      <div className="px-3 py-1.5 text-[15px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Markt Wählen', locale)}</div>
                      {options.map(opt => (
                        <button key={opt} onClick={() => { setTopMarket5(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[15px] hover:bg-gray-100 flex items-center gap-2">
                          <div className="w-4">{topMarket5 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                          <span className={topMarket5 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
            )}
            <div className="w-[52px]" />
          </div>
        )}


        {/* Tournaments + Matches */}
        {loading ? (
          <div className="min-h-[200px] flex justify-center items-center bg-white rounded-b">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#cc0000] border-t-transparent"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-[14px]">
            Keine Begegnungen in dieser Sportart verfügbar.
          </div>
        ) : (
          Object.entries(
            [...events].sort((a, b) => eventStartMs(a) - eventStartMs(b)).reduce((acc, ev) => {
              const t = ev.event.tournament_name || 'Andere';
              const c = ev.event.category_name ?? '';
              const key = c ? `${c} / ${t}` : t;
              if (!acc[key]) acc[key] = [];
              acc[key].push(ev);
              return acc;
            }, {} as Record<string, EventSnapshot[]>)
          ).map(([tournament, eventsGroup]) => (
            <React.Fragment key={tournament}>
              <div className="px-5 py-1.5 bg-[#f7f8f9] border-t border-b border-gray-200 flex items-center justify-between mt-[-1px]">
                <div className="flex items-center gap-2">
                  {(() => { const flagUrl = getCountryFlagUrl(eventsGroup[0]?.event?.country_code); return flagUrl ? <img src={flagUrl} alt="" className="w-6 h-[18px] object-cover flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : null; })()}
                  <span className="text-[12px] font-bold text-gray-900">{tournament}</span>
                  <svg className="w-3 h-3 text-gray-400 ml-1" viewBox="0 0 24 24" fill="currentColor"><path d="M5 9h4v11H5zm6-5h4v16h-4zm6 8h4v8h-4z" /></svg>
                </div>
                <div className="flex items-center text-[13px] font-bold text-gray-400">
                  {renderMarketColHeader(topMarket1, "w-[151px]")}
                  <div className={`flex-shrink-0${hideSecondMarket ? ' hidden min-[1500px]:block' : ''}`}>{renderMarketColHeader(topMarket2, "w-[151px]")}</div>
                  <div className={`flex-shrink-0 ${hideSecondMarket ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>{renderMarketColHeader(topMarket3, "w-[151px]")}</div>
                  {!hideSecondMarket && <div className="hidden min-[1650px]:block flex-shrink-0">{renderMarketColHeader(topMarket4, "w-[151px]")}</div>}
                  {!hideSecondMarket && <div className="hidden min-[1750px]:block flex-shrink-0">{renderMarketColHeader(topMarket5, "w-[151px]")}</div>}
                  <div className="w-[52px]" />
                </div>
              </div>
              {eventsGroup.map((ev) => {
                const total = ev.markets?.length ?? 0;

                const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
                const d = starts ? new Date(starts) : null;
                const now = new Date();
                const tomorrow = new Date(now);
                tomorrow.setDate(now.getDate() + 1);
                let dayLabel = d ? d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '';
                if (d?.toDateString() === now.toDateString()) dayLabel = 'Heute';
                else if (d?.toDateString() === tomorrow.toDateString()) dayLabel = 'Morgen';
                const timeStr = d ? formatTime(d.toISOString()) : '';

                return (
                  <div key={ev.event_id} className="px-5 py-1 flex items-center hover:bg-gray-50 transition border-b border-gray-200 bg-white last:border-b-0">
                    <div className="w-[36px] flex-shrink-0 cursor-pointer pt-0.5 text-center items-start justify-start text-left" onClick={() => handleEventClick(ev)}>
                      <div className="text-[13px] font-medium text-gray-500 mb-0.5">{dayLabel}</div>
                      <div className="text-[13px] font-medium text-gray-500">{timeStr}</div>
                    </div>

                    <div className="flex-1 min-w-0 flex flex-col justify-center cursor-pointer border-r border-gray-100 pr-4 pl-2" onClick={() => handleEventClick(ev)}>
                      <div className="flex items-center gap-2 min-w-0">
                          {ev.event?.home?.crest_url ? <img src={ev.event.home.crest_url} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" /> : <img src="/default-crest.svg" alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" />}
                          <span className="text-[15px] font-medium text-gray-900 truncate">{ev.event?.home?.name}</span>
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                          {ev.event?.away?.crest_url ? <img src={ev.event.away.crest_url} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" /> : <img src="/default-crest.svg" alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" />}
                          <span className="text-[15px] font-medium text-gray-900 truncate">{ev.event?.away?.name}</span>
                      </div>
                    </div>

                    {renderMarketColCells(ev, topMarket1, "w-[151px]")}
                    <div className={`flex-shrink-0${hideSecondMarket ? ' hidden min-[1500px]:block' : ''}`}>
                      {renderMarketColCells(ev, topMarket2, "w-[151px]")}
                    </div>
                    <div className={`flex-shrink-0 ${hideSecondMarket ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>
                      {renderMarketColCells(ev, topMarket3, "w-[151px]")}
                    </div>
                    {!hideSecondMarket && (
                      <>
                        <div className="hidden min-[1650px]:block flex-shrink-0">
                          {renderMarketColCells(ev, topMarket4, "w-[151px]")}
                        </div>
                        <div className="hidden min-[1750px]:block flex-shrink-0">
                          {renderMarketColCells(ev, topMarket5, "w-[151px]")}
                        </div>
                      </>
                    )}

                    <button onClick={() => handleEventClick(ev)} className="w-[52px] flex items-center justify-end text-[15px] font-medium text-gray-500 hover:text-gray-800 transition">
                      <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                      {total} ›
                    </button>
                  </div>
                );
              })}
            </React.Fragment>
          ))
        )}

        {showViewAll && onViewAll && (
          <div className="bg-[#f7f8f9] border-t border-gray-200 px-5 py-1 flex justify-end">
            <button onClick={onViewAll} className="text-[15px] font-bold text-gray-500 hover:text-gray-900 transition flex items-center">
              Alle <span className="text-lg leading-none ml-1">›</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
